// Central app config.
//
// Multi-cell aware. The API base URL is resolved at RUNTIME, per user:
//
//   1. `cellApiBaseUrl` in localStorage — the URL of the cell that OWNS the
//      signed-in user (written by `cellRouting.js` from the JWT `cell` claim /
//      the verify response). This is what makes every `config.API_URL` call
//      site automatically talk to the user's own regional cell.
//   2. the build-time `REACT_APP_API_URL` (else `config.json`) — the BAKED
//      endpoint. Used as the initial login/router endpoint and as the fallback
//      for anonymous requests, single-cell installs, and local dev.
//
// Call sites should `import config from '.../config'` and read `config.API_URL`
// (a runtime getter) or call `getApiUrl()`. The named `API_URL` / `BASE_API_URL`
// exports are the BAKED value (the login/router endpoint), used by cellRouting
// itself to bootstrap the cell map before any cell URL is known.

import jsonConfig from './config.json';

// Baked endpoint: the login/router origin + fallback. Never changes at runtime.
const BAKED_API_URL = process.env.REACT_APP_API_URL || jsonConfig.API_URL;

// The signed-in user's cell base URL, if we've resolved one. Read directly from
// localStorage (not via cellRouting) to avoid an import cycle.
function runtimeCellBaseUrl() {
  try {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem('cellApiBaseUrl') || '';
    }
  } catch (e) {
    /* localStorage unavailable (SSR / privacy mode) */
  }
  return '';
}

export function getApiUrl() {
  return runtimeCellBaseUrl() || BAKED_API_URL;
}

// Baked exports (login/router endpoint). Prefer `config.API_URL` (runtime) at
// normal call sites; use these only when you explicitly need the baked origin.
export const API_URL = BAKED_API_URL;
export const BASE_API_URL = BAKED_API_URL;

const config = {
  ...jsonConfig,
  // Runtime getter: per-user cell URL when known, else the baked endpoint.
  get API_URL() {
    return getApiUrl();
  },
  BASE_API_URL: BAKED_API_URL,
};

export default config;
